import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StopState } from './pick-session.js';

/**
 * BERISH oqimining qoralamasi.
 *
 * Serverga hech narsa bosqichma-bosqich yuborilmaydi: skladchi kodni
 * skanerlaydi, miqdorni kiritadi, rasm oladi, oluvchini yozadi va faqat
 * OXIRIDA bitta so'rov ketadi. Sababi ishning o'zida: berish — bo'linmas
 * hujjat, uning yarmini yozib qo'yib ketib bo'lmaydi. Yarim yozilgan
 * dalolatnoma "berildimi yoki yo'qmi" degan savolni javobsiz qoldirardi.
 *
 * Rasmlar bundan MUSTASNO: ular tanlangan zahoti yuklanadi (`fileId`),
 * chunki bir necha megabaytli faylni oxirgi tugmaga saqlab qo'yish
 * "Berildi" ni bir necha soniyaga cho'zardi — skladchi esa uni bosgandan
 * keyin darhol javob kutadi.
 */

export type HandoverStep = 'scan' | 'pick' | 'issue';

/**
 * Bosqichlar TARTIBI — oldinga va orqaga yurish shu ro'yxatdan hisoblanadi.
 *
 * Ilgari beshta bosqich bor edi: skanerlash → miqdor → rasm → oluvchi →
 * tasdiq. Uchtasi olib tashlandi va har birining sababi boshqa:
 *
 *   - **miqdor** hech narsa SO'RAMASDI — u faqat ko'rsatardi. Beriladigan
 *     son tasdiqlanganiga aynan teng bo'lishi shart, ya'ni skladchi u yerda
 *     hech narsa qila olmasdi;
 *   - **oluvchi** ikki marta ish edi: buyurtmachi kimni yuborishini
 *     talabnomada allaqachon ko'rsatgan (`throughPerson`), lavozim esa
 *     ko'pincha bilinmasdi va baribir qog'ozga tushmasdi;
 *   - **tasdiq** qolgan ikkitasining takrori edi.
 *
 * Qolgani ikkita: kodni topish va berish. Berish ekranida hammasi bir
 * joyda — nima beriladi, kim oladi, rasm va tugma. Bir ekranda
 * ko'rilmagan narsani odam baribir tekshirmaydi.
 */
export const HANDOVER_STEPS: readonly HandoverStep[] = ['scan', 'pick', 'issue'];

/** Berilayotgan satr: hujjatdagi miqdorlar va skladchi kiritgani. */
export interface HandoverLineDraft {
  lineId: string;
  lineNo: number;
  nomenclatureId: string;
  requestedQty: number;
  /** Tasdiqlangan miqdor — SHUNDAN ko'p berib bo'lmaydi. */
  approvedQty: number;
  issuedQty: number;
  /**
   * Tasdiqlangandan KAM berilganda — sababi.
   *
   * Majburiyligi shu yerda ham, serverda ham (`planIssue`) bir xil
   * tekshiriladi va bu takror ataylab: ekran tugmani oldindan o'chiradi,
   * server esa ekranni chetlab o'tgan so'rovni rad etadi.
   */
  note: string | null;
}

/**
 * Foto-hisobot rasmi.
 *
 * `fileId` — serverdagi identifikator: u kelmaguncha rasm dalolatnomaga
 * biriktirilmaydi. `previewUrl` esa mahalliy (`URL.createObjectURL`) va u
 * yuklanish tugashini kutmaydi — skladchi olgan suratini DARHOL ko'rishi
 * kerak, aks holda u ikkinchi marta suratga oladi.
 */
export interface HandoverPhotoDraft {
  id: string;
  name: string;
  previewUrl: string;
  fileId: string | null;
  status: 'uploading' | 'ready' | 'failed';
}

/** Skanerlangandan keyin topilgan talabnoma — oqim shundan boshlanadi. */
export interface PickedRequisition {
  id: string;
  docNo: string;
  /**
   * Qog'ozdagi «ТРЕБОВАНИЕ №» — buxgalter qo'ygan raqam.
   *
   * Mahsulot AYNAN shu raqam bo'yicha beriladi: olib ketayotgan odamning
   * qo'lida shu raqamli qog'oz turadi va skladchi ikkovini solishtiradi.
   * Talabnoma raqami (`docNo`) ichki hujjat raqami va u qog'ozda yo'q.
   */
  formNo: string | null;
  /**
   * «Kim orqali» — buyurtmachi ko'rsatgan olib ketuvchi.
   *
   * Dalolatnomadagi oluvchi aynan shundan olinadi (serverda ham). Skladchi
   * uni qayta yozmaydi: u allaqachon hujjatda turgan ma'lumot.
   */
  throughPerson: string | null;
  lines: Omit<HandoverLineDraft, 'issuedQty' | 'note'>[];
}

export interface HandoverState {
  step: HandoverStep;
  /** Skanerlangan kod — topilmasa ham ekranda ko'rinib turadi. */
  code: string | null;
  requisitionId: string | null;
  docNo: string | null;
  /** Qog'ozdagi «ТРЕБОВАНИЕ №» — mahsulot shu raqam bo'yicha beriladi. */
  formNo: string | null;
  /** «Kim orqali» — dalolatnomadagi oluvchi shundan olinadi. */
  throughPerson: string | null;
  lines: HandoverLineDraft[];
  photos: HandoverPhotoDraft[];
  comment: string;
  /** Xato — i18n KALITI (matn emas): ekran matni lug'atda turadi. */
  error: string | null;
  /**
   * YIG'IB OLISH seansi — qaysi o'rin yopilgan.
   *
   * Ekranning O'ZIDA emas, do'konda: skladchi javonlar orasida yurib,
   * telefonni cho'ntagiga solib-olib turadi va sahifa bir necha marta
   * qayta yuklanishi mumkin. Ekran holatida saqlansa, har yangilashda
   * yig'ib olingan o'rinlar noldan boshlanardi.
   *
   * `key` — rejaning o'zginasi (`lineId:cellId:batchId` juftliklari):
   * reja o'zgargan bo'lsa (boshqa skladchi qabulni qayta yozgan),
   * eski holat yangi qatorlarga yopishib qolmasligi kerak.
   */
  pickSession: { key: string; states: StopState[] } | null;

  setCode: (code: string) => void;
  pick: (requisition: PickedRequisition) => void;
  addPhoto: (photo: Pick<HandoverPhotoDraft, 'id' | 'name' | 'previewUrl'>) => void;
  markPhoto: (id: string, patch: Partial<Pick<HandoverPhotoDraft, 'status' | 'fileId'>>) => void;
  removePhoto: (id: string) => void;
  setComment: (comment: string) => void;
  /** Yig'ib olish ekrani yopilganda — haqiqatda olingan miqdorlar. */
  setIssued: (rows: readonly { lineId: string; issuedQty: number; note: string | null }[]) => void;
  /** Yig'ib olish seansini saqlaydi — qayta yuklashdan omon chiqishi uchun. */
  setPickSession: (session: { key: string; states: StopState[] }) => void;
  next: () => void;
  back: () => void;
  reset: () => void;
}

/** Miqdorlar `numeric(14,3)` — uchdan ortiq kasr baribir bazada yo'qoladi. */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Shu bosqichdan CHIQISHGA to'sqinlik qilayotgan sabab (i18n kaliti) yoki
 * `null`.
 *
 * Sof funksiya va ataylab: ekran tugmani shu bilan o'chiradi, `next()` esa shu
 * bilan to'xtaydi — ikkalasi bitta qoidadan oziqlanadi. Ikki joyda yozilsa,
 * o'chirilgan tugma bosilib ketishi (yoki aksincha) mumkin bo'lardi.
 */
export const stepBlocker = (state: HandoverState): string | null => {
  switch (state.step) {
    case 'scan':
      return state.requisitionId === null ? 'handover.errorNoRequisition' : null;
    case 'pick':
      return state.lines.length === 0 ? 'handover.errorNoLines' : null;
    case 'issue': {
      if (state.lines.length === 0) return 'handover.errorNoLines';
      /* Miqdor TAHRIRLANMAYDI, lekin tekshiruv qoladi: u tasdiqlangan
         miqdor nolga teng bo'lgan satrni ushlaydi — bunday satr
         berilmasligi kerak edi va uni ekran jimgina o'tkazib yubormasin. */
      /* Tasdiqlangandan KO'P berib bo'lmaydi — hech qanday sabab bilan:
         bunday mahsulot talabnoma zanjiridan umuman o'tmagan bo'lardi. */
      if (state.lines.some((line) => round3(line.issuedQty) > round3(line.approvedQty))) {
        return 'handover.errorQtyTooMuch';
      }
      /* Musbat bo'lmagan satr — tasdiqlangan miqdori nolga teng satr
         berilmasligi kerak edi va ekran uni jimgina o'tkazmasin. */
      if (state.lines.some((line) => round3(line.issuedQty) <= 0)) {
        return 'handover.errorQtyEmpty';
      }
      /* KAM berish mumkin, lekin faqat SABAB bilan: e'tiroz kam berishga
         emas, uni JIMGINA qilishga. */
      if (
        state.lines.some(
          (line) =>
            round3(line.issuedQty) < round3(line.approvedQty) &&
            (line.note === null || line.note.trim() === ''),
        )
      ) {
        return 'handover.errorShortReason';
      }
      if (state.photos.length === 0) return 'handover.errorPhotoRequired';
      return state.photos.every((photo) => photo.status === 'ready')
        ? null
        : 'handover.errorPhotoPending';
    }
  }
};

const INITIAL = {
  step: 'scan' as HandoverStep,
  code: null,
  requisitionId: null,
  docNo: null,
  formNo: null,
  throughPerson: null,
  lines: [] as HandoverLineDraft[],
  photos: [] as HandoverPhotoDraft[],
  comment: '',
  error: null,
  pickSession: null as { key: string; states: StopState[] } | null,
};

/**
 * Qoralama QAYTA YUKLASHDAN omon chiqadi.
 *
 * Skladchi javonlar orasida yuradi, telefonni cho'ntagiga solib-olib
 * turadi va sahifa bir necha marta qayta yuklanishi mumkin — tarmoq
 * uzilib-ulanganda ham. Xotiradagi qoralama esa har yangilashda
 * ro'yxatga qaytarib yuborardi va yig'ib olingan o'rinlar noldan
 * boshlanardi: javonlar bo'ylab ikkinchi marta yurish.
 *
 * RASMLAR saqlanmaydi. Ularning ko'rinishi `blob:` manzil orqali beriladi
 * va u sahifa yopilishi bilan o'ladi — saqlansa, qayta yuklashdan keyin
 * ro'yxatda ochilmaydigan rasmlar turardi. Yuklanganlari serverda
 * qoladi, lekin ularni qayta biriktirish boshqa ish; skladchi suratni
 * qaytadan oladi va bu bir tegish.
 *
 * XATO ham saqlanmaydi: u o'tgan urinishning javobi va yangi seansda
 * sababsiz turib qolardi.
 */
const STORAGE_KEY = 'eombor.handover.draft';

export const useHandoverStore = create<HandoverState>()(
  persist(
    (set, get) => ({
  ...INITIAL,

  setCode: (code) => set({ code, error: null }),

  /**
   * Talabnoma topildi — oqim BERISH ekraniga o'tadi.
   *
   * Miqdorlar TASDIQLANGANI bilan to'ldiriladi: to'liq berish eng ko'p
   * uchraydigan hol va skladchi hech narsa yozmasdan davom eta olishi kerak.
   * Bo'sh qoldirilsa, u har satrga qo'lda raqam kiritishga majbur bo'lardi.
   */
  pick: (requisition) =>
    set({
      /* Talabnoma topildi → YIG'IB OLISH ekrani. Berish (rasm va
         «Berildi») undan KEYIN: mahsulot hali javondan olinmagan. */
      step: 'pick',
      requisitionId: requisition.id,
      docNo: requisition.docNo,
      formNo: requisition.formNo,
      throughPerson: requisition.throughPerson,
      /* Beriladigan miqdor TASDIQLANGANIGA teng va o'zgarmaydi: u
         zanjirda uch marta kelishilgan (korxona, sklad, buxgalteriya) va
         oxirgi qadamda uni jimgina kamaytirish shu kelishuvni buzish
         bo'lardi. Shuning uchun uni o'zgartiradigan amal umuman yo'q. */
      /* Miqdor jimlikda TASDIQLANGANIGA teng: odatiy berishda skladchi
         unga tegmaydi va yig'ib olish ekrani uni shunday yopadi. */
      lines: requisition.lines.map((line) => ({
        ...line,
        issuedQty: line.approvedQty,
        note: null,
      })),
      error: null,
    }),

  addPhoto: (photo) =>
    set((state) => ({
      photos: [...state.photos, { ...photo, fileId: null, status: 'uploading' }],
      error: null,
    })),

  markPhoto: (id, patch) =>
    set((state) => ({
      photos: state.photos.map((photo) => (photo.id === id ? { ...photo, ...patch } : photo)),
    })),

  removePhoto: (id) =>
    set((state) => ({ photos: state.photos.filter((photo) => photo.id !== id), error: null })),

  setComment: (comment) => set({ comment, error: null }),

  /**
   * Yig'ib olingan miqdorlar — satrlarga yoziladi.
   *
   * Ro'yxatda YO'Q satr tegilmay qoladi: yig'ib olish ekrani faqat
   * manzili bor satrlarni biladi va manzilsiz satr (bunday bo'lmasligi
   * kerak, lekin bo'lib qolsa) jimgina nolga tushib ketmasin.
   */
  setIssued: (rows) =>
    set((state) => {
      const byLine = new Map(rows.map((row) => [row.lineId, row]));
      return {
        lines: state.lines.map((line) => {
          const row = byLine.get(line.lineId);
          return row === undefined
            ? line
            : { ...line, issuedQty: row.issuedQty, note: row.note };
        }),
        error: null,
      };
    }),

  next: () => {
    const state = get();
    const blocker = stepBlocker(state);
    if (blocker !== null) {
      set({ error: blocker });
      return;
    }
    const index = HANDOVER_STEPS.indexOf(state.step);
    const nextStep = HANDOVER_STEPS[index + 1];
    if (nextStep === undefined) return;
    set({ step: nextStep, error: null });
  },

  /**
   * Orqaga — kiritilgan hech narsa YO'QOLMAYDI.
   *
   * Skladchi rasmni qayta ko'rish yoki miqdorni tuzatish uchun qaytadi va
   * bu qadam qolganini bekor qilsa, u har safar hammasini boshidan yozardi.
   */
  back: () => {
    const index = HANDOVER_STEPS.indexOf(get().step);
    const previous = HANDOVER_STEPS[index - 1];
    if (previous === undefined) return;
    set({ step: previous, error: null });
  },

  setPickSession: (pickSession) => set({ pickSession }),

  reset: () => set({ ...INITIAL }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        step: state.step,
        requisitionId: state.requisitionId,
        docNo: state.docNo,
        formNo: state.formNo,
        throughPerson: state.throughPerson,
        lines: state.lines,
        comment: state.comment,
        pickSession: state.pickSession,
      }),
      /* Talabnomasiz qoralama MA'NOSIZ: saqlangan qiymat buzilgan yoki
         eski tuzilishda bo'lsa, oqim boshidan boshlanadi — yarim
         tiklangan ekrandan ko'ra shu halolroq. */
      merge: (saved, current) => {
        const draft = saved as Partial<HandoverState> | undefined;
        if (draft?.requisitionId === undefined || draft.requisitionId === null) return current;
        return { ...current, ...draft, photos: [], error: null };
      },
    },
  ),
);
